import { useEffect, useState } from "react";
import { DayPicker } from "react-day-picker";
import { format, parseISO, differenceInCalendarDays, subMonths, addMonths, startOfDay, startOfMonth, addDays } from "date-fns";
import { zhTW } from "date-fns/locale";
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";

// Shared Lakeze date picker — continuous range band, zh-TW, weeks start Monday
const toD = (v) => (!v ? undefined : v instanceof Date ? startOfDay(v) : parseISO(String(v).slice(0, 10)));
export const ymd = (d) => format(d, "yyyy-MM-dd");

const CLS = {
  months: "flex gap-6", month: "space-y-2", caption: "flex justify-center relative items-center h-8",
  caption_label: "text-sm font-semibold text-slate-800", nav: "flex items-center",
  nav_button: "h-7 w-7 inline-flex items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-30",
  nav_button_previous: "absolute left-0", nav_button_next: "absolute right-0",
  table: "border-collapse", head_row: "flex", head_cell: "w-9 text-[11px] font-normal text-slate-400",
  row: "flex mt-1", cell: "lk-cell relative p-0 w-9 h-9 text-center",
  day: "lk-day h-9 w-9 text-sm rounded-full text-slate-700 hover:bg-orange-100 disabled:hover:bg-transparent",
  day_disabled: "text-slate-300 cursor-not-allowed", day_hidden: "invisible", day_outside: "invisible",
};
const MOD_CLS = { lk_start: "lk-start", lk_end: "lk-end", lk_mid: "lk-mid", lk_preview: "lk-preview", today: "lk-today" };
const Icons = { IconLeft: () => <ChevronLeft className="h-4 w-4" />, IconRight: () => <ChevronRight className="h-4 w-4" /> };
const dayContent = ({ date }) => <span data-testid={`cal-day-${ymd(date)}`}>{date.getDate()}</span>;

function useNarrow() {
  const [n, setN] = useState(() => window.innerWidth < 900);
  useEffect(() => { const f = () => setN(window.innerWidth < 900); window.addEventListener("resize", f); return () => window.removeEventListener("resize", f); }, []);
  return n;
}

function limits(min, max, disableFuture) {
  const today = startOfDay(new Date());
  const maxD = [toD(max), disableFuture ? today : undefined].filter(Boolean).sort((a, b) => a - b)[0];
  const minD = toD(min);
  return { minD, maxD, disabled: [minD && { before: minD }, maxD && { after: maxD }].filter(Boolean) };
}

export function rangeHeader(from, to) {
  if (!from) return "請選擇開始日期";
  if (!to) return "請選擇結束日期";
  return `${ymd(from)} – ${ymd(to)}（共 ${differenceInCalendarDays(to, from) + 1} 天）`;
}

export function DateRangeCalendar({ since, until, onApply, onCancel, min, max, disableFuture = true, testid = "date-range-cal" }) {
  const narrow = useNarrow();
  const [from, setFrom] = useState(toD(since));
  const [to, setTo] = useState(toD(until));
  const [hover, setHover] = useState();
  const { minD, maxD, disabled } = limits(min, max, disableFuture);
  const [month, setMonth] = useState(() => {
    // 2 months: [anchor, next] when next month is selectable, else [prev, anchor]
    const anchor = startOfMonth(to || from || maxD || new Date());
    if (narrow) return anchor;
    const m = maxD && addMonths(anchor, 1) > maxD ? subMonths(anchor, 1) : anchor;
    return from && from < m ? startOfMonth(from) : m;
  });
  const onDay = (d, mods) => {
    if (mods.disabled) return;
    if (!from || to) { setFrom(d); setTo(undefined); } else if (d < from) setFrom(d); else setTo(d);
  };
  const end = to || (from && hover && hover >= from ? hover : undefined);
  const modifiers = {
    lk_start: from ? [from] : [], lk_end: end ? [end] : from ? [from] : [],
    // explicit range (an inverted/empty {after,before} interval in react-day-picker matches everything outside it)
    lk_mid: from && end && differenceInCalendarDays(end, from) >= 2 ? [{ from: addDays(from, 1), to: addDays(end, -1) }] : [],
    lk_preview: !to && from && end ? [{ from, to: end }] : [],
  };
  return (
    <div className="p-3" data-testid={testid}>
      <div className={`mb-2 text-sm font-mono ${from && to ? "text-slate-900 font-semibold" : "text-[#C2410C]"}`} data-testid={`${testid}-header`}>{rangeHeader(from, to)}</div>
      <div onMouseLeave={() => setHover(undefined)}>
        <DayPicker className="lk-cal" classNames={CLS} modifiers={modifiers} modifiersClassNames={MOD_CLS} components={{ ...Icons, DayContent: dayContent }}
          locale={zhTW} weekStartsOn={1} numberOfMonths={narrow ? 1 : 2} month={month} onMonthChange={setMonth}
          fromMonth={minD} toMonth={maxD} disabled={disabled} onDayClick={onDay} onDayMouseEnter={(d) => setHover(d)} showOutsideDays={false} />
      </div>
      <div className="mt-2 pt-2 border-t border-slate-100 flex justify-end gap-2">
        <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={onCancel} data-testid={`${testid}-cancel`}>取消</Button>
        <Button size="sm" disabled={!from || !to} onClick={() => onApply({ since: ymd(from), until: ymd(to) })}
          className="h-8 text-xs bg-[#F26A1F] hover:bg-[#D95710] text-white" data-testid={`${testid}-apply`}>套用</Button>
      </div>
    </div>
  );
}

const TRIGGER = "inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-slate-200 bg-white text-xs font-mono text-slate-800 hover:bg-slate-50";

export function DateRangeField({ since, until, onChange, testid = "date-range-field", min, max, disableFuture = true, className = "" }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className={`${TRIGGER} ${className}`} data-testid={testid}>
          <CalendarIcon className="h-3.5 w-3.5 text-slate-500" />{since && until ? `${since} ~ ${until}` : "選擇日期區間"}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-white border-slate-200" align="start">
        {open && <DateRangeCalendar since={since} until={until} min={min} max={max} disableFuture={disableFuture} testid={`${testid}-cal`}
          onCancel={() => setOpen(false)} onApply={(r) => { onChange(r); setOpen(false); }} />}
      </PopoverContent>
    </Popover>
  );
}

export function DateField({ value, onChange, testid = "date-field", min, max, disableFuture = false, className = "" }) {
  const [open, setOpen] = useState(false);
  const sel = toD(value);
  const { minD, maxD, disabled } = limits(min, max, disableFuture);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className={`${TRIGGER} ${className}`} data-testid={testid}>
          <CalendarIcon className="h-3.5 w-3.5 text-slate-500" />{value || "選擇日期"}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-3 bg-white border-slate-200" align="start" data-testid={`${testid}-cal`}>
        <DayPicker className="lk-cal" classNames={CLS} modifiers={{ lk_start: sel ? [sel] : [], lk_end: sel ? [sel] : [] }} modifiersClassNames={MOD_CLS}
          components={{ ...Icons, DayContent: dayContent }} locale={zhTW} weekStartsOn={1} defaultMonth={sel} fromMonth={minD} toMonth={maxD}
          disabled={disabled} showOutsideDays={false} onDayClick={(d, m) => { if (m.disabled) return; onChange(ymd(d)); setOpen(false); }} />
      </PopoverContent>
    </Popover>
  );
}
